/**
 * DNS state per center.
 *
 * Two things this page refuses to do:
 *
 * * **Show an empty table when `configured` is false.** No Cloudflare token
 *   means nothing was checked; an empty table means every record is missing.
 *   They demand opposite responses, so they are worded as opposites.
 * * **Treat `proxied: true` as a detail.** Cloudflare's orange cloud only
 *   carries HTTP/HTTPS on a fixed port list. A proxied record silently stops
 *   scanners C-STOREing to that center's `:4242`, and archive downloads start
 *   running into Cloudflare's request limits. It is an error, ranked with the
 *   missing records, not a column of "true".
 *
 * The redesign (Carbon removed) keeps both refusals and adds three more.
 *
 * *One derived verdict per row.* The old table made the operator read four
 * columns — found, type, this-VM, proxy — and assemble the verdict themselves.
 * A row now leads with a single health pill computed in one place
 * (`recordHealth`), ordered worst-first: **proxied**, then **missing**, then
 * **wrong target**, then **not fully checked**. Proxying is ranked above a
 * missing record deliberately: a missing record fails loudly and obviously,
 * while an orange-clouded one resolves, answers HTTPS, and looks healthy right
 * up until a scanner cannot C-STORE to it.
 *
 * *Not-checked is its own verdict, never a pass.* `proxied` and `points_at_vm`
 * are tri-state, so a record whose target could not be evaluated — no
 * `expected_ip` on the controller — reads "Not fully checked" in grey rather
 * than falling through to OK. The same reasoning is why `expected_ip` is stated
 * next to a wrong target: "points somewhere else" is only actionable beside
 * what right would have been, and when it is null the page says so at the top
 * instead of quietly marking every row unverified.
 *
 * *A job's log is the job's evidence.* Each provisioning job renders its own
 * `log` as a timeline instead of a single collapsed line, because the reason a
 * job is parked is in the entries and not in its state name. `blocked` is drawn
 * as a failure and offered **no** retry control: retrying cannot change a record
 * that points somewhere else, and a disabled-looking button an operator can
 * still click would bury that behind an attempt counter. `managed: false` is
 * labelled "Adopted" wherever the job appears — this tool did not create that
 * record and must never delete it.
 *
 * Healthy is quiet throughout: a clean record's verdict is a grey dot and the
 * word OK, and colour is spent only on the rows that need a human.
 *
 * ── Compaction pass (daily operator, expert) ──────────────────────────────
 *
 * What changed, and what deliberately did not:
 *
 * * The header subtitle and the two explanatory paragraphs above the table are
 *   gone. The honesty callouts stay — "nothing was checked" and "no VM address
 *   configured, so targets were not verified" are the page's whole point and
 *   are only tightened, never removed.
 * * The KPI strip is the filter. Proxied / no record / pointing elsewhere /
 *   not checked are `aria-pressed` buttons that narrow the table, so the
 *   numbers and the way to act on them are the same control. That is what
 *   replaced the missing-records callout: the count is a tile, the domains are
 *   one click away in the table.
 * * The table dropped its "This VM" and "Cloudflare proxy" columns. Both were
 *   the inputs to the health pill that already leads the row; both are still
 *   stated in full, tri-state, in the record panel. Zone id and expected
 *   address moved onto the Records card header, replacing the strip that used
 *   to sit between the tiles and the table.
 * * Jobs are a table, not a stack of cards. A job row carries its newest log
 *   line — the reason it is parked, which is the thing the old inline card was
 *   for — and its full evidence (key/values plus the whole timeline) opens in a
 *   detail panel. Every row is reachable by keyboard through its own open
 *   button, not only by clicking the row.
 * * Untouched by design: the blocked-will-not-retry wording, the absence of a
 *   retry control on a blocked job, "Adopted" vs "Created here", one click is
 *   one attempt, and the owner-role note beside every write.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge, type Tone, TriStateBadge } from '../components/Badge';
import { Callout } from '../components/Callout';
import { Freshness } from '../components/Freshness';
import { PageHeader } from '../components/PageHeader';
import { StatGrid, StatTile, type MetricTone } from '../components/StatTile';
import { EmptyBlock, ErrorBlock, SkeletonRows, StaleBanner } from '../components/States';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader, KeyValue, KeyValueRow } from '../components/ui/Card';
import { CopyButton } from '../components/ui/CopyButton';
import { DataTable, type Column } from '../components/ui/DataTable';
import { DetailPanel } from '../components/ui/DetailPanel';
import { FilterBar, SegmentedFilter } from '../components/ui/FilterBar';
import { TextInput } from '../components/ui/Form';
import { ChevronRightIcon, RenewIcon } from '../components/ui/Icon';
import { ActivityTimeline, type TimelineStatus } from '../components/ui/Timeline';
import { apiPost, describeError } from '../lib/api';
import {
  formatDateTime,
  formatNumber,
  formatRelative,
  isoAttribute,
  pluralise,
  UNKNOWN,
} from '../lib/format';
import { useResource, type ResourceState } from '../lib/useResource';
import type { DnsJobResponse, DnsRecordState, DnsResponse } from '../types/api';

import '../styles/page-dns.css';

/* ── the verdict ──────────────────────────────────────────────────────────── */

type Health = 'proxied' | 'missing' | 'wrong-target' | 'unchecked' | 'ok';

/** Worst first. Proxied outranks missing — see the header comment. */
const HEALTH_RANK: Record<Health, number> = {
  proxied: 0,
  missing: 1,
  'wrong-target': 2,
  unchecked: 3,
  ok: 4,
};

const HEALTH_LABEL: Record<Health, string> = {
  proxied: 'Proxied',
  missing: 'No record',
  'wrong-target': 'Wrong target',
  unchecked: 'Not fully checked',
  ok: 'OK',
};

const HEALTH_TONE: Record<Health, Tone> = {
  proxied: 'critical',
  missing: 'serious',
  'wrong-target': 'warning',
  unchecked: 'neutral',
  ok: 'good',
};

const HEALTH_WHY: Record<Health, string> = {
  proxied:
    "Cloudflare's proxy carries HTTP and HTTPS on a fixed port list only. While the orange cloud is on, scanners cannot C-STORE to :4242 and archive downloads run into Cloudflare's request limits.",
  missing: 'No record for this domain exists in the zone, so the center is unreachable by name however healthy its container is.',
  'wrong-target': 'The record exists but resolves to an address that is not this VM.',
  unchecked:
    'The record exists, but at least one of the proxy and target answers came back unknown — it was not checked, which is not the same as passing.',
  ok: 'A DNS-only record pointing at this VM.',
};

/**
 * The single place a record's verdict is decided.
 *
 * Order matters and is the header comment's ranking. The `unchecked` branch is
 * last but is *not* a fallthrough to OK: a null proxy or target answer means
 * the check did not produce a result, and rendering that as a pass is how an
 * operator ships a broken zone with a green screen in front of them.
 */
function recordHealth(record: DnsRecordState): Health {
  if (record.proxied === true) return 'proxied';
  if (!record.record_found) return 'missing';
  if (record.points_at_vm === false) return 'wrong-target';
  if (record.points_at_vm === null || record.proxied === null) return 'unchecked';
  return 'ok';
}

interface DnsRow extends DnsRecordState {
  health: Health;
}

/** Quiet when fine, coloured only when it is not. */
function HealthPill({ health }: { health: Health }) {
  if (health === 'ok') {
    return (
      <span className="dns-ok" title={HEALTH_WHY.ok}>
        <span className="dns-ok__dot" aria-hidden="true" />
        OK
      </span>
    );
  }
  return (
    <Badge tone={HEALTH_TONE[health]} small title={HEALTH_WHY[health]}>
      {HEALTH_LABEL[health]}
    </Badge>
  );
}

/* ── jobs ─────────────────────────────────────────────────────────────────── */

const JOB_TONE: Record<string, Tone> = {
  verified: 'good',
  verifying: 'info',
  pending: 'neutral',
  blocked: 'critical',
  failed: 'critical',
};

/** `blocked` is drawn as a failure. It is never drawn, or worded, as a pass. */
const JOB_TIMELINE_STATUS: Record<string, TimelineStatus> = {
  verified: 'ok',
  verifying: 'active',
  pending: 'pending',
  blocked: 'failed',
  failed: 'failed',
};

const JOB_MEANING: Record<string, string> = {
  pending: 'Queued. The next click creates or re-reads the record.',
  verifying: "Waiting on the zone's own nameservers to answer with the new record.",
  verified: 'The record exists, is DNS-only, and resolves to this VM.',
  blocked:
    'Stopped on purpose. The domain already has a record pointing somewhere else, and this tool will not overwrite it.',
  failed: 'The last attempt errored. Running another step retries it.',
};

/** Worst first, so the jobs table opens on the ones that are parked. */
const JOB_RANK: Record<string, number> = {
  blocked: 0,
  failed: 1,
  verifying: 2,
  pending: 3,
  verified: 4,
};

/** True only for the one state that a further click cannot move. */
function isBlocked(job: DnsJobResponse): boolean {
  return job.state === 'blocked';
}

/** The newest log line — the reason a job is parked, in the row itself. */
function newestEntry(job: DnsJobResponse): string | null {
  if (job.log.length === 0) return null;
  return job.log[job.log.length - 1] ?? null;
}

/**
 * The job's log as evidence.
 *
 * Only the newest entry gets a time: `updated_at` is when the job last changed,
 * which is that entry's timestamp and no other's. Stamping it on every line
 * would invent per-entry times the API does not return. Earlier entries are
 * `info` — history, with no claim about whether each one passed.
 */
function jobTimelineItems(job: DnsJobResponse) {
  const last = job.log.length - 1;
  if (job.log.length === 0) {
    return [
      {
        id: `${job.slug}-empty`,
        status: JOB_TIMELINE_STATUS[job.state] ?? 'pending',
        title: 'No log entries yet',
        detail: JOB_MEANING[job.state] ?? undefined,
        time: formatRelative(job.updated_at),
      },
    ];
  }
  return job.log.map((line, index) => ({
    id: `${job.slug}-${index}`,
    status: (index === last
      ? JOB_TIMELINE_STATUS[job.state] ?? 'info'
      : 'info') as TimelineStatus,
    title: line,
    time: index === last ? formatRelative(job.updated_at) : undefined,
  }));
}

/** A relative time that keeps the absolute one in reach. */
function When({ value }: { value: string | null }) {
  if (!value) return <span className="muted">{UNKNOWN}</span>;
  return (
    <time dateTime={isoAttribute(value)} title={formatDateTime(value)}>
      {formatRelative(value)}
    </time>
  );
}

/** Created-here / Adopted, wherever a job appears. Adopted is never deleted. */
function OwnershipBadge({ job }: { job: DnsJobResponse }) {
  if (job.managed) {
    return (
      <Badge tone="info" small title="This tool created this record.">
        Created here
      </Badge>
    );
  }
  if (job.record_id) {
    return (
      <Badge
        tone="neutral"
        small
        title="This tool did not create this record and will never delete it."
      >
        Adopted
      </Badge>
    );
  }
  return null;
}

/**
 * The one mutation on this page, hoisted so the record panel and the
 * provisioning card share it.
 *
 * One click is one state-machine step, not a background run: the POST returns
 * the job after a single attempt, and what comes back is what is shown. There
 * is no spinner standing in for a process the operator cannot inspect.
 */
function useProvision(onDone: () => void) {
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const provision = useCallback(
    async (slug: string) => {
      const trimmed = slug.trim();
      if (!trimmed) return;
      setBusySlug(trimmed);
      setError(null);
      try {
        await apiPost<DnsJobResponse>(
          `/api/dns/${encodeURIComponent(trimmed)}/provision`,
        );
        onDone();
      } catch (cause) {
        setError(describeError(cause));
      } finally {
        setBusySlug(null);
      }
    },
    [onDone],
  );

  return { provision, busySlug, error };
}

interface Provisioner {
  provision: (slug: string) => void | Promise<void>;
  busySlug: string | null;
  error: string | null;
}

/* ── page ─────────────────────────────────────────────────────────────────── */

/**
 * The narrow verdict filters exist so a KPI tile can *be* the filter it counts.
 * The segmented control still offers the four coarse groups, and a narrow
 * filter reports itself as "Needs attention" there rather than leaving the
 * radio group with nothing selected.
 */
type RecordFilter =
  | 'all'
  | 'attention'
  | 'proxied'
  | 'missing'
  | 'wrong'
  | 'unchecked'
  | 'ok';

/** A KPI that is also the filter it describes — same markup as StatTile. */
function FilterTile({
  label,
  value,
  hint,
  tone,
  active,
  onToggle,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: MetricTone;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className={`metric dns-kpi${tone ? ` metric--${tone}` : ''}`}
      aria-pressed={active}
      onClick={onToggle}
    >
      <span className="metric__head">
        <span className="metric__label">{label}</span>
      </span>
      <span className="metric__value">{value}</span>
      {hint ? <span className="metric__hint">{hint}</span> : null}
    </button>
  );
}

export function DnsPage() {
  const dns = useResource<DnsResponse>('/api/dns');
  const jobs = useResource<DnsJobResponse[]>('/api/dns/jobs');

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<RecordFilter>('all');
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const [openJobSlug, setOpenJobSlug] = useState<string | null>(null);

  // `reload` is stable across renders, so the mutation hook is too.
  const provisioner = useProvision(jobs.reload);

  const configured = dns.data?.configured ?? false;
  const expectedIp = dns.data?.expected_ip ?? null;
  const zoneId = dns.data?.zone_id ?? null;

  const rows = useMemo<DnsRow[]>(
    () =>
      (dns.data?.records ?? []).map((record) => ({
        ...record,
        health: recordHealth(record),
      })),
    [dns.data],
  );

  const counts = useMemo(() => {
    const tally = { proxied: 0, missing: 0, wrong: 0, unchecked: 0, ok: 0 };
    for (const row of rows) {
      if (row.health === 'proxied') tally.proxied += 1;
      else if (row.health === 'missing') tally.missing += 1;
      else if (row.health === 'wrong-target') tally.wrong += 1;
      else if (row.health === 'unchecked') tally.unchecked += 1;
      else tally.ok += 1;
    }
    return tally;
  }, [rows]);

  const attention = counts.proxied + counts.missing + counts.wrong;
  const proxiedRows = useMemo(() => rows.filter((r) => r.health === 'proxied'), [rows]);

  const filtered = useMemo(() => {
    switch (filter) {
      case 'attention':
        return rows.filter(
          (r) =>
            r.health === 'proxied' || r.health === 'missing' || r.health === 'wrong-target',
        );
      case 'proxied':
        return rows.filter((r) => r.health === 'proxied');
      case 'missing':
        return rows.filter((r) => r.health === 'missing');
      case 'wrong':
        return rows.filter((r) => r.health === 'wrong-target');
      case 'unchecked':
        return rows.filter((r) => r.health === 'unchecked');
      case 'ok':
        return rows.filter((r) => r.health === 'ok');
      default:
        return rows;
    }
  }, [rows, filter]);

  const jobsBySlug = useMemo(
    () => new Map((jobs.data ?? []).map((job) => [job.slug, job])),
    [jobs.data],
  );

  // Jobs are counted here as well as inside the card so the KPI strip can say
  // how many are parked without the operator scrolling to find out. Unloaded is
  // UNKNOWN, never 0 — an unfetched list is not an empty one.
  const jobRows = jobs.data ?? [];
  const jobsAttention = jobRows.filter((job) => job.needs_attention).length;
  const jobsBlocked = jobRows.filter(isBlocked).length;

  const open = openSlug ? rows.find((r) => r.slug === openSlug) : undefined;
  const openJob = openSlug ? jobsBySlug.get(openSlug) : undefined;
  const openJobRow = openJobSlug ? jobsBySlug.get(openJobSlug) : undefined;

  // The two panels are one surface: opening either closes the other.
  const showRecord = useCallback((slug: string) => {
    setOpenJobSlug(null);
    setOpenSlug(slug);
  }, []);
  const showJob = useCallback((slug: string) => {
    setOpenSlug(null);
    setOpenJobSlug(slug);
  }, []);

  const toggle = useCallback(
    (next: RecordFilter) => setFilter((current) => (current === next ? 'all' : next)),
    [],
  );

  const columns: ReadonlyArray<Column<DnsRow, string>> = useMemo(
    () => [
      {
        key: 'health',
        header: 'Health',
        value: (row) => HEALTH_RANK[row.health],
        render: (row) => <HealthPill health={row.health} />,
      },
      {
        key: 'domain',
        header: 'Domain',
        // Slug joins the sort value so the search box can find a record by
        // center as its placeholder promises.
        value: (row) => `${row.domain} ${row.slug}`,
        render: (row) => (
          <div className="cell-stack">
            <span className="cell-stack__primary mono">{row.domain}</span>
            {/* A real link, so middle-click and open-in-new-tab still work. */}
            <Link
              to={`/centers/${row.slug}`}
              className="cell-stack__secondary"
              onClick={(event) => event.stopPropagation()}
            >
              {row.slug}
            </Link>
          </div>
        ),
      },
      {
        key: 'record_type',
        header: 'Type',
        value: (row) => row.record_type,
        render: (row) =>
          row.record_type ? (
            <span className="chip chip--mono">{row.record_type}</span>
          ) : (
            <span className="muted">{UNKNOWN}</span>
          ),
      },
      {
        key: 'content',
        header: 'Points to',
        value: (row) => row.content,
        render: (row) => (
          <div className="cell-stack">
            {row.content ? (
              <span className="cell-stack__primary mono">{row.content}</span>
            ) : (
              <span className="muted">{UNKNOWN}</span>
            )}
            {/* Only where the two disagree: "wrong" is unactionable without
                the address that would have been right. */}
            {row.points_at_vm === false && expectedIp ? (
              <span className="cell-stack__secondary dns-expected">
                expected <span className="mono">{expectedIp}</span>
              </span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'ttl',
        header: 'TTL',
        numeric: true,
        value: (row) => row.ttl,
        // Null TTL is an em dash, never a 0 an operator would read as "instant".
        render: (row) => formatNumber(row.ttl),
      },
      {
        key: 'open',
        header: <span className="visually-hidden">Record detail</span>,
        sortable: false,
        width: '2.5rem',
        // The row click is a convenience; this is the keyboard path to the
        // panel, and the panel is where the tri-state proxy and target answers
        // that used to be columns now live.
        render: (row) => (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={ChevronRightIcon}
            iconDescription={`Open DNS detail for ${row.domain}`}
            onClick={() => showRecord(row.slug)}
          />
        ),
      },
    ],
    [expectedIp, showRecord],
  );

  const stale = Boolean(dns.error && dns.data);

  /**
   * The one-glance answer, worst first — and never "clean" for a page that did
   * not check anything, which is why `configured` is the first branch and
   * "not fully checked" outranks the all-clear.
   */
  const statusBadge = (() => {
    if (!dns.data) return null;
    if (!configured) return <Badge tone="neutral">Nothing checked</Badge>;
    if (rows.length === 0) return <Badge tone="neutral">No records</Badge>;
    if (counts.proxied > 0) {
      return <Badge tone="critical">{`${counts.proxied} proxied`}</Badge>;
    }
    if (attention > 0) {
      const noun = pluralise(attention, 'record');
      const verb = attention === 1 ? 'needs' : 'need';
      return <Badge tone="warning">{`${attention} ${noun} ${verb} attention`}</Badge>;
    }
    if (counts.unchecked > 0) {
      return <Badge tone="neutral">{`${counts.unchecked} not fully checked`}</Badge>;
    }
    return <Badge tone="good">All records clean</Badge>;
  })();

  /** What the segmented control shows while a narrow tile filter is active. */
  const segment: RecordFilter =
    filter === 'proxied' || filter === 'missing' || filter === 'wrong'
      ? 'attention'
      : filter;

  return (
    <>
      <PageHeader
        title="DNS"
        status={statusBadge}
        actions={
          <Freshness
            checkedAt={dns.fetchedAt}
            refreshing={dns.refreshing}
            onRefresh={() => {
              dns.reload();
              jobs.reload();
            }}
            label="Checked"
          />
        }
      />

      {stale ? <StaleBanner error={dns.error} onRetry={dns.reload} /> : null}

      {dns.loading && !dns.data ? (
        <SkeletonRows rows={4} />
      ) : dns.error && !dns.data ? (
        <ErrorBlock what="DNS state" error={dns.error} onRetry={dns.reload} />
      ) : dns.data && !configured ? (
        // Not "no records" — "no check". This is the page's most important
        // state, so it stands alone above everything and no table, count or
        // tile is rendered beside it to be misread as a result.
        <Callout tone="neutral" title="DNS checking is not configured — nothing was checked">
          <p>
            The controller has no Cloudflare API token, so not one record was looked
            up. Blank here means unchecked, not clean. Set{' '}
            <code>FLEET_CLOUDFLARE_API_TOKEN</code>, <code>FLEET_CLOUDFLARE_ZONE_ID</code>{' '}
            and <code>FLEET_VM_PUBLIC_IP</code> on the controller, then reload.
          </p>
        </Callout>
      ) : dns.data ? (
        <>
          {proxiedRows.length > 0 ? (
            <Callout
              tone="critical"
              live
              title={`${proxiedRows.length} ${pluralise(
                proxiedRows.length,
                'record is',
                'records are',
              )} proxied — scanners cannot C-STORE to :4242 while the orange cloud is on`}
            >
              <p className="row">
                {proxiedRows.map((record) => (
                  <Link
                    key={record.slug}
                    to={`/centers/${record.slug}`}
                    className="chip chip--mono dns-chip-link"
                  >
                    {record.domain}
                  </Link>
                ))}
              </p>
            </Callout>
          ) : null}

          {rows.length > 0 && !expectedIp ? (
            // expected_ip null ⇒ points_at_vm is null on every row. Saying so
            // once here is why the rows can say "Not checked" rather than
            // leaving the operator to guess why nothing verified.
            <Callout tone="neutral" title="No VM address configured — targets were not verified">
              <p>
                The controller has no <code>FLEET_VM_PUBLIC_IP</code>, so it had nothing
                to compare each record against. Every target verdict below is{' '}
                <em>not checked</em>, not <em>correct</em>.
              </p>
            </Callout>
          ) : null}

          <StatGrid>
            <StatTile
              label="Records"
              value={formatNumber(rows.length)}
              hint="one per center in the fleet config"
            />
            <FilterTile
              label="Proxied"
              value={formatNumber(counts.proxied)}
              tone={counts.proxied > 0 ? 'critical' : 'neutral'}
              hint="orange cloud breaks DICOM"
              active={filter === 'proxied'}
              onToggle={() => toggle('proxied')}
            />
            <FilterTile
              label="No record"
              value={formatNumber(counts.missing)}
              tone={counts.missing > 0 ? 'serious' : 'neutral'}
              hint="unreachable by name"
              active={filter === 'missing'}
              onToggle={() => toggle('missing')}
            />
            <FilterTile
              label="Pointing elsewhere"
              value={formatNumber(counts.wrong)}
              tone={counts.wrong > 0 ? 'warning' : 'neutral'}
              hint={expectedIp ? `not ${expectedIp}` : 'not this VM'}
              active={filter === 'wrong'}
              onToggle={() => toggle('wrong')}
            />
            <FilterTile
              label="Not checked"
              value={formatNumber(counts.unchecked)}
              hint="an unknown answer, not a pass"
              active={filter === 'unchecked'}
              onToggle={() => toggle('unchecked')}
            />
            <StatTile
              label="Jobs parked"
              // An unfetched job list is unknown, not zero.
              value={jobs.data ? formatNumber(jobsAttention) : UNKNOWN}
              tone={jobsBlocked > 0 ? 'critical' : jobsAttention > 0 ? 'warning' : 'neutral'}
              hint={
                jobsBlocked > 0
                  ? `${jobsBlocked} blocked — will not retry`
                  : 'jobs needing attention'
              }
            />
          </StatGrid>

          {rows.length === 0 ? (
            <EmptyBlock title="The zone was checked and contained no matching records">
              A Cloudflare token is configured and the lookup succeeded, but no center
              domain resolved to a record in this zone.
            </EmptyBlock>
          ) : (
            <>
              <FilterBar
                search={search}
                onSearch={setSearch}
                searchPlaceholder="Search center, domain, type or address…"
                searchLabel="Search DNS records"
                count={
                  filtered.length === rows.length
                    ? `${formatNumber(rows.length)} ${pluralise(rows.length, 'record')}`
                    : `${formatNumber(filtered.length)} of ${formatNumber(rows.length)}`
                }
              >
                <SegmentedFilter<RecordFilter>
                  legend="Filter records by health"
                  value={segment}
                  onChange={setFilter}
                  options={[
                    { value: 'all', label: 'All', count: rows.length },
                    { value: 'attention', label: 'Needs attention', count: attention },
                    { value: 'unchecked', label: 'Not checked', count: counts.unchecked },
                    { value: 'ok', label: 'OK', count: counts.ok },
                  ]}
                />
              </FilterBar>

              <Card flush>
                <CardHeader
                  title="Records"
                  count={formatNumber(rows.length)}
                  actions={<ZoneBar zoneId={zoneId} expectedIp={expectedIp} />}
                />
                <DataTable<DnsRow, string>
                  rows={filtered}
                  columns={columns}
                  getRowId={(row) => row.slug}
                  label="DNS records"
                  initialSortKey="health"
                  search={search}
                  onRowClick={(row) => showRecord(row.slug)}
                  isRowActive={(row) => row.slug === openSlug}
                  emptyFiltered={
                    <EmptyBlock title="Nothing matches those filters">
                      {search ? (
                        <>
                          No record matches <strong>{search}</strong>
                          {filter !== 'all' ? ' with that health' : ''}.
                        </>
                      ) : (
                        'No record has that health right now.'
                      )}
                    </EmptyBlock>
                  }
                />
              </Card>
            </>
          )}
        </>
      ) : null}

      <ProvisionPanel jobs={jobs} provisioner={provisioner} onOpenJob={showJob} />

      <DetailPanel
        open={open !== undefined}
        title={open?.domain ?? ''}
        subtitle={open?.slug}
        onClose={() => setOpenSlug(null)}
        footer={
          open ? (
            <Link to={`/centers/${open.slug}`} className="btn btn--primary btn--md">
              Open full center page
              <ChevronRightIcon size={14} />
            </Link>
          ) : null
        }
      >
        {open ? (
          <RecordPanelBody
            record={open}
            expectedIp={expectedIp}
            zoneId={zoneId}
            job={openJob}
            provisioner={provisioner}
          />
        ) : null}
      </DetailPanel>

      {/* A job's evidence, out of the list and into a panel — the list keeps
          only its newest line, which is the part that is read at a glance. */}
      <DetailPanel
        open={openJobRow !== undefined}
        title={openJobRow?.domain ?? ''}
        subtitle={openJobRow ? `${openJobRow.slug} · provisioning job` : undefined}
        onClose={() => setOpenJobSlug(null)}
        footer={
          openJobRow ? (
            <Link to={`/centers/${openJobRow.slug}`} className="btn btn--primary btn--md">
              Open full center page
              <ChevronRightIcon size={14} />
            </Link>
          ) : null
        }
      >
        {openJobRow ? <JobBody job={openJobRow} provisioner={provisioner} /> : null}
      </DetailPanel>
    </>
  );
}

/** Zone and expected address, on the table's own header rather than a strip. */
function ZoneBar({ zoneId, expectedIp }: { zoneId: string | null; expectedIp: string | null }) {
  return (
    <div className="dns-zone">
      <span className="dns-zone__item">
        <span className="dns-zone__label">Zone</span>
        {zoneId ? (
          <>
            <span className="mono dns-zone__value" title={zoneId}>
              {zoneId}
            </span>
            <CopyButton text={zoneId} label="Copy" />
          </>
        ) : (
          <span className="muted">{UNKNOWN}</span>
        )}
      </span>
      <span className="dns-zone__item">
        <span className="dns-zone__label">Expected</span>
        {expectedIp ? (
          <span className="mono">{expectedIp}</span>
        ) : (
          <span className="muted">{UNKNOWN}</span>
        )}
      </span>
    </div>
  );
}

/* ── record detail ────────────────────────────────────────────────────────── */

function RecordPanelBody({
  record,
  expectedIp,
  zoneId,
  job,
  provisioner,
}: {
  record: DnsRow;
  expectedIp: string | null;
  zoneId: string | null;
  job: DnsJobResponse | undefined;
  provisioner: Provisioner;
}) {
  return (
    <>
      <section className="stack">
        <div className="row">
          <HealthPill health={record.health} />
          {job ? <OwnershipBadge job={job} /> : null}
        </div>
        <p className="dns-why">{HEALTH_WHY[record.health]}</p>
        {record.health === 'proxied' ? (
          <Callout tone="critical" title="Turn the orange cloud off for this record">
            Cloudflare&rsquo;s proxy only forwards a fixed list of HTTP/HTTPS ports.
            DICOM on <span className="mono">4242</span> is not on that list, so a
            scanner pointed at this domain cannot C-STORE at all while proxying is on.
          </Callout>
        ) : null}
      </section>

      <section className="stack">
        <h3 className="section__subtitle">Record</h3>
        {/* Domain, center and zone are the panel's title, subtitle and card
            header — they are not repeated here. What is here is what the table
            no longer shows. */}
        <KeyValue>
          <KeyValueRow label="Type">
            {record.record_type ? (
              <span className="mono">{record.record_type}</span>
            ) : (
              <span className="muted">{UNKNOWN}</span>
            )}
          </KeyValueRow>
          <KeyValueRow label="Points to">
            {record.content ? (
              <span className="mono">{record.content}</span>
            ) : (
              <span className="muted">{UNKNOWN}</span>
            )}
          </KeyValueRow>
          <KeyValueRow label="Expected address">
            {expectedIp ? (
              <span className="mono">{expectedIp}</span>
            ) : (
              <span className="muted">Not configured — nothing to compare against</span>
            )}
          </KeyValueRow>
          <KeyValueRow label="Points at this VM">
            <TriStateBadge
              value={record.points_at_vm}
              trueLabel="Yes"
              falseLabel="No"
              falseTone="warning"
              trueTone="neutral"
              nullLabel="Not checked"
            />
          </KeyValueRow>
          <KeyValueRow label="Cloudflare proxy">
            <TriStateBadge
              value={record.proxied}
              trueLabel="Proxied — breaks DICOM"
              falseLabel="DNS only"
              trueTone="critical"
              falseTone="neutral"
              nullLabel="Not checked"
            />
          </KeyValueRow>
          <KeyValueRow label="Record in zone">
            {record.record_found ? 'Found' : <span className="muted">None</span>}
          </KeyValueRow>
          <KeyValueRow label="TTL">
            <span className="numeric">{formatNumber(record.ttl)}</span>
          </KeyValueRow>
          <KeyValueRow label="Zone">
            {zoneId ? <span className="mono">{zoneId}</span> : <span className="muted">{UNKNOWN}</span>}
          </KeyValueRow>
        </KeyValue>
      </section>

      <section className="stack">
        <h3 className="section__subtitle">Provisioning</h3>
        {job ? (
          <JobBody job={job} provisioner={provisioner} />
        ) : (
          <>
            <p className="dns-why">
              No job for this center. Jobs live in memory and do not survive a controller
              restart, so an absent job says nothing about the record.
            </p>
            <div className="row">
              <Button
                kind="secondary"
                size="sm"
                renderIcon={RenewIcon}
                disabled={provisioner.busySlug !== null}
                onClick={() => provisioner.provision(record.slug)}
              >
                {provisioner.busySlug === record.slug ? 'Working…' : 'Create record'}
              </Button>
              <span className="field__help muted">
                One click, one attempt. Owner role required.
              </span>
            </div>
          </>
        )}
      </section>
    </>
  );
}

/* ── provisioning ─────────────────────────────────────────────────────────── */

/**
 * Provisioning jobs, and the one control that writes to the zone.
 *
 * Each click advances a job by a single attempt rather than starting a
 * background run, so what the operator sees is what has actually happened —
 * not a spinner standing in for a process they cannot inspect. A job that ends
 * `blocked` will not be retried by clicking again: retrying cannot change a
 * record that points somewhere else, and pretending otherwise would bury the
 * reason under an attempt counter. So a blocked job is given no retry control
 * at all rather than a disabled one.
 *
 * The jobs are a table now. Each row still carries its newest log line, because
 * that line is why the job is where it is; the rest of the evidence is one
 * click — or one Enter on the row's open button — away in the panel.
 */
function ProvisionPanel({
  jobs,
  provisioner,
  onOpenJob,
}: {
  jobs: ResourceState<DnsJobResponse[]>;
  provisioner: Provisioner;
  onOpenJob: (slug: string) => void;
}) {
  const [slug, setSlug] = useState('');

  const rows = jobs.data ?? [];
  const busy = provisioner.busySlug !== null;
  const blocked = rows.filter(isBlocked).length;
  const attention = rows.filter((job) => job.needs_attention).length;

  const columns: ReadonlyArray<Column<DnsJobResponse, string>> = useMemo(
    () => [
      {
        key: 'state',
        header: 'State',
        value: (job) => JOB_RANK[job.state] ?? 9,
        render: (job) => (
          <span className="row">
            <Badge tone={JOB_TONE[job.state] ?? 'neutral'} small title={JOB_MEANING[job.state]}>
              {job.state}
            </Badge>
            <OwnershipBadge job={job} />
          </span>
        ),
      },
      {
        key: 'domain',
        header: 'Domain',
        value: (job) => `${job.domain} ${job.slug}`,
        render: (job) => (
          <div className="cell-stack">
            <span className="cell-stack__primary mono">{job.domain}</span>
            <Link
              to={`/centers/${job.slug}`}
              className="cell-stack__secondary"
              onClick={(event) => event.stopPropagation()}
            >
              {job.slug}
            </Link>
          </div>
        ),
      },
      {
        key: 'log',
        header: 'Newest log entry',
        value: (job) => newestEntry(job),
        render: (job) => {
          const entry = newestEntry(job);
          if (!entry) return <span className="muted">No entries yet</span>;
          return (
            <span className="mono dns-cell-log" title={entry}>
              {entry}
            </span>
          );
        },
      },
      {
        key: 'attempts',
        header: 'Attempts',
        numeric: true,
        value: (job) => job.attempts,
        render: (job) => formatNumber(job.attempts),
      },
      {
        key: 'updated_at',
        header: 'Updated',
        render: (job) => <When value={job.updated_at} />,
      },
      {
        key: 'open',
        header: <span className="visually-hidden">Job detail</span>,
        sortable: false,
        width: '2.5rem',
        render: (job) => (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={ChevronRightIcon}
            iconDescription={`Open the provisioning job for ${job.domain}`}
            onClick={() => onOpenJob(job.slug)}
          />
        ),
      },
    ],
    [onOpenJob],
  );

  return (
    <Card>
      <CardHeader
        title="Provisioning"
        count={rows.length > 0 ? formatNumber(rows.length) : undefined}
        subtitle="Creates a DNS-only (grey cloud) A record for this VM. A record already pointing here is adopted and never rewritten; one pointing elsewhere blocks. Owner role required."
        actions={
          attention > 0 ? (
            <Badge tone={blocked > 0 ? 'critical' : 'warning'} small>
              {attention} needing attention
            </Badge>
          ) : null
        }
      />
      <CardBody>
        <div className="stack">
          {provisioner.error ? (
            <Callout tone="critical" title="Could not provision" live>
              {provisioner.error}
            </Callout>
          ) : null}

          <form
            className="dns-provision"
            onSubmit={(event) => {
              event.preventDefault();
              void provisioner.provision(slug);
            }}
          >
            <TextInput
              id="dns-provision-slug"
              labelText="Center slug"
              // The label is hidden, not dropped: the sentence beside the
              // button is the description, wired up by hand so it is still
              // announced with the field.
              hideLabel
              aria-describedby="dns-provision-help"
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              placeholder="center27"
              autoComplete="off"
              spellCheck={false}
            />
            <Button type="submit" kind="primary" size="md" disabled={busy || !slug.trim()}>
              {provisioner.busySlug === slug.trim() ? 'Working…' : 'Provision / retry'}
            </Button>
            <span id="dns-provision-help" className="field__help muted">
              One click is one attempt. Owner role required.
            </span>
          </form>

          {jobs.error && jobs.data ? (
            <StaleBanner error={jobs.error} onRetry={jobs.reload} />
          ) : null}
        </div>
      </CardBody>

      {jobs.loading && !jobs.data ? (
        <CardBody>
          <SkeletonRows rows={2} />
        </CardBody>
      ) : jobs.error && !jobs.data ? (
        <CardBody>
          <ErrorBlock
            what="the provisioning jobs"
            error={jobs.error}
            onRetry={jobs.reload}
          />
        </CardBody>
      ) : rows.length === 0 ? (
        <EmptyBlock title="No provisioning jobs">
          Jobs are held in memory and do not survive a controller restart. Re-running one
          is safe: it re-reads the zone and adopts whatever is already there.
        </EmptyBlock>
      ) : (
        <DataTable<DnsJobResponse, string>
          rows={rows}
          columns={columns}
          getRowId={(job) => job.slug}
          label="Provisioning jobs"
          initialSortKey="state"
          onRowClick={(job) => onOpenJob(job.slug)}
        />
      )}
    </Card>
  );
}

/** One job's facts, its evidence, and — unless it is blocked — its next step. */
function JobBody({ job, provisioner }: { job: DnsJobResponse; provisioner: Provisioner }) {
  const blockedJob = isBlocked(job);
  const items = jobTimelineItems(job);

  return (
    <div className="stack dns-job__body">
      <div className="row">
        <Badge tone={JOB_TONE[job.state] ?? 'neutral'} small>
          {job.state}
        </Badge>
        <OwnershipBadge job={job} />
      </div>
      <p className="dns-why">{JOB_MEANING[job.state] ?? 'State reported by the controller.'}</p>

      {blockedJob ? (
        // No retry control here on purpose: another attempt cannot change the
        // answer, and offering one would read as "try again" when the fix is
        // in Cloudflare.
        <Callout tone="critical" title="Blocked — another attempt cannot change this">
          <p>
            The domain already has a record pointing somewhere other than this VM, and
            this tool will not overwrite a record it did not create. Repoint or remove
            the record in Cloudflare, then run a step again.
          </p>
        </Callout>
      ) : null}

      {job.last_error ? (
        <p className="panel-error">
          <strong>Last error:</strong> {job.last_error}
        </p>
      ) : null}

      <KeyValue>
        <KeyValueRow label="Target address">
          {job.vm_ip ? <span className="mono">{job.vm_ip}</span> : <span className="muted">{UNKNOWN}</span>}
        </KeyValueRow>
        <KeyValueRow label="Attempts">{formatNumber(job.attempts)}</KeyValueRow>
        <KeyValueRow label="Next attempt">
          {blockedJob ? (
            <span className="muted">None — blocked</span>
          ) : (
            <When value={job.next_attempt_at} />
          )}
        </KeyValueRow>
        <KeyValueRow label="Record id">
          {job.record_id ? (
            <span className="mono">{job.record_id}</span>
          ) : (
            <span className="muted">{UNKNOWN}</span>
          )}
        </KeyValueRow>
        <KeyValueRow label="Ownership">
          {job.managed ? (
            'Created by this tool'
          ) : job.record_id ? (
            'Adopted — never deleted from here'
          ) : (
            <span className="muted">No record yet</span>
          )}
        </KeyValueRow>
        <KeyValueRow label="Updated">
          <When value={job.updated_at} />
        </KeyValueRow>
      </KeyValue>

      {/* Focusable, so a long log can be scrolled without a pointer — the
          same treatment the table primitive gives its scroll box. */}
      <div
        className="dns-job__log"
        tabIndex={0}
        role="region"
        aria-label={`Provisioning log for ${job.domain}`}
      >
        <ActivityTimeline items={items} label={`Provisioning log for ${job.domain}`} />
      </div>

      {blockedJob ? null : (
        <div className="row">
          <Button
            kind="secondary"
            size="sm"
            renderIcon={RenewIcon}
            disabled={provisioner.busySlug !== null}
            onClick={() => provisioner.provision(job.slug)}
          >
            {/* The label tracks what the next attempt will actually do. The
                first attempt on a center with no record WRITES one to
                Cloudflare; later ones only re-check, adopting a record that
                already points here. Calling both "Check" would understate the
                first, which is the one that is hard to undo. */}
            {provisioner.busySlug === job.slug
              ? 'Working…'
              : job.state === 'pending'
                ? 'Create record'
                : 'Check again'}
          </Button>
          <span className="field__help muted">
            One click, one attempt. Owner role required.
          </span>
        </div>
      )}
    </div>
  );
}
